import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

// UI Components
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription } from '../ui/sheet';
import { useToast } from '../../hooks/use-toast';

// Icons
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  User,
  Mail,
  Phone,
  MapPin,
  GraduationCap,
  Home,
  MessageSquare,
  AlertCircle,
  Loader2,
  UserCheck,
  Award,
  Bus,
  XCircle,
  Flag,
} from 'lucide-react';

// Hooks and Utils
import { useStudent, useUpdateStudent, useFlagStudent, useResolveIssue } from '../../hooks/useStudents';
import { useUserPermissions } from '../../hooks/useAuth';
import { useStudentFormData } from '../../hooks/useStudents';
import { apiClient } from '../../lib/api';
import { invalidateQueries } from '../../lib/queryClient';
import { useTranslation } from 'react-i18next';
import i18n from '../../lib/i18n';
import { FriendshipPanel } from './FriendshipPanel';
import { InterviewScoringPanel } from './InterviewScoringPanel';
import { PaymentsPanel } from './PaymentsPanel';
import { HouseAssignmentPanel, FriendGroupCard } from './HouseAssignmentPanel';
import { ClassAssignmentPanel } from './ClassAssignmentPanel';
import { EmailLogPanel } from './EmailLogPanel';
import { LinkedHistoryPanel } from './LinkedHistoryPanel';

// ---------------------------------------------------------------------------
// Validation schema
// ---------------------------------------------------------------------------
const studentSchema = z.object({
  first_name: z.string()
    .min(2, i18n.t('students.validation.firstNameMinLength'))
    .max(50, i18n.t('students.validation.firstNameMaxLength'))
    .regex(/^[a-zA-ZÀ-ÿ\u0100-\u017F\s'\u2019-]+$/, i18n.t('students.validation.firstNameInvalid')),
  last_name: z.string()
    .min(2, i18n.t('students.validation.lastNameMinLength'))
    .max(50, i18n.t('students.validation.lastNameMaxLength'))
    .regex(/^[a-zA-ZÀ-ÿ\u0100-\u017F\s'\u2019-]+$/, i18n.t('students.validation.lastNameInvalid')),
  birthday: z.string()
    .min(1, i18n.t('students.validation.birthdayRequired'))
    .refine((d) => { const b = new Date(d); return b <= new Date(); }, i18n.t('students.validation.birthdayFuture')),
  student_email: z.string().email(i18n.t('students.validation.emailInvalid')).optional().or(z.literal('')),
  parent_name: z.string().max(100, i18n.t('students.validation.parentNameMaxLength'))
    .regex(/^$|^[a-zA-ZÀ-ÿ\u0100-\u017F\s'\u2019-]+$/, i18n.t('students.validation.parentNameInvalid'))
    .optional().or(z.literal('')),
  parent_email: z.string().min(1, i18n.t('students.validation.parentEmailRequired')).email(i18n.t('students.validation.parentEmailInvalid')),
  parent_phone: z.string()
    .regex(/^$|^[+]?[0-9\s-()]+$/, i18n.t('students.validation.parentPhoneInvalid'))
    .optional().or(z.literal('')),
  school: z.string().optional(),
  home_county: z.string().min(1, i18n.t('students.validation.homeCountyRequired')),
  address_1: z.string().optional(),
  address_2: z.string().optional(),
  address_3: z.string().optional(),
  address_4: z.string().optional(),
  eircode: z.string().optional(),
  notes: z.string().optional(),
  attended_before: z.boolean().optional(),
  when_attended: z.string().optional(),
  gender: z.string().min(1, i18n.t('students.validation.genderRequired')),
  course: z.string().min(1, i18n.t('students.validation.courseRequired')),
  school_year: z.string().min(1, i18n.t('students.validation.schoolYearRequired')),
  travel_required: z.boolean().optional(),
  travel_location: z.string().optional(),
  is_cinnire: z.boolean().optional(),
  has_friends: z.boolean().optional(),
}).superRefine((data, ctx) => {
  if (data.travel_required && (!data.travel_location || data.travel_location.trim() === '')) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: i18n.t('students.validation.travelLocationRequired'), path: ['travel_location'] });
  }
});

// ---------------------------------------------------------------------------
// Unified section card — same header style across all cards
// ---------------------------------------------------------------------------
const SectionCard = ({ title, icon: Icon, children, className = '' }) => (
  <Card className={className}>
    <CardHeader className="bg-muted/40 border-b border-border py-3 px-4">
      <CardTitle className="flex items-center text-sm font-semibold text-foreground">
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F28482]/10 mr-3">
          <Icon className="h-4 w-4 text-[#F28482]" />
        </span>
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-4">
      {children}
    </CardContent>
  </Card>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const StudentDetailNew = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reinstateDialogOpen, setReinstateDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  const [flagNote, setFlagNote] = useState('');

  const { hasPermission, userType } = useUserPermissions();
  const { data: student, isLoading, error, refetch: refetchStudent } = useStudent(uuid);
  const { schoolYears, courses, travelLocations } = useStudentFormData();
  const updateStudentMutation = useUpdateStudent();
  const flagStudentMutation = useFlagStudent();
  const resolveIssueMutation = useResolveIssue();

  const permissions = {
    canView: hasPermission('view_students') || true,
    canEdit: hasPermission('manage_students') || ['ADMIN', 'TEACHER', 'OFFICE'].includes(userType),
    canViewFinancials: hasPermission('view_financials') || ['ADMIN', 'OFFICE'].includes(userType),
    canViewParentInfo: hasPermission('view_parent_info') || ['ADMIN', 'OFFICE'].includes(userType),
    canViewFullAddress: hasPermission('view_full_address') || ['ADMIN', 'OFFICE'].includes(userType),
    canViewNotes: hasPermission('view_student_notes') || ['ADMIN', 'TEACHER', 'OFFICE'].includes(userType),
    canScoreInterviews: ['ADMIN', 'OFFICE', 'INTERVIEWER'].includes(userType),
    canViewCommunications: hasPermission('view_communications') || ['ADMIN', 'OFFICE'].includes(userType),
  };

  const form = useForm({
    resolver: zodResolver(studentSchema),
    defaultValues: {
      first_name: '', last_name: '', birthday: '', student_email: '',
      parent_name: '', parent_email: '', parent_phone: '', school: '',
      home_county: '', address_1: '', address_2: '', address_3: '',
      address_4: '', eircode: '', notes: '', attended_before: false,
      when_attended: '', gender: '', course: '', school_year: '',
      travel_required: false, travel_location: '', is_cinnire: false, has_friends: false,
    },
  });

  useEffect(() => {
    if (student) {
      form.reset({
        first_name: student.first_name || '', last_name: student.last_name || '',
        birthday: student.birthday || '', student_email: student.student_email || '',
        parent_name: student.parent_name || '', parent_email: student.parent_email || '',
        parent_phone: student.parent_phone || '', school: student.school || '',
        home_county: student.home_county || '', address_1: student.address_1 || '',
        address_2: student.address_2 || '', address_3: student.address_3 || '',
        address_4: student.address_4 || '', eircode: student.eircode || '',
        notes: student.notes || '', attended_before: student.attended_before || false,
        when_attended: student.when_attended || '', gender: student.gender || '',
        course: student.course || '', school_year: student.school_year || '',
        travel_required: student.travel_required || false,
        travel_location: student.travel_location || '',
        is_cinnire: student.is_cinnire || false, has_friends: student.has_friends || false,
      });
    }
  }, [student, form]);

  // Handlers
  const handleSave = async (data) => {
    if (!permissions.canEdit) {
      toast({ title: t('students.error.accessDenied'), description: t('students.error.cannotEdit', { userType }), variant: 'destructive' });
      return;
    }
    try {
      await updateStudentMutation.mutateAsync({
        id: student.uuid,
        first_name: data.first_name, last_name: data.last_name,
        birthday: data.birthday || student.birthday,
        student_email: data.student_email || '',
        parent_name: data.parent_name || '', parent_email: data.parent_email || '',
        parent_phone: data.parent_phone || '', school: data.school || '',
        home_county: data.home_county || '', address_1: data.address_1 || '',
        address_2: data.address_2 || '', address_3: data.address_3 || '',
        address_4: data.address_4 || '', eircode: data.eircode || '',
        notes: data.notes || '', travel_required: data.travel_required,
        travel_location: data.travel_required ? (data.travel_location || null) : null,
        is_cinnire: data.is_cinnire, has_friends: data.has_friends,
        gender: data.gender, school_year: data.school_year, course: data.course,
      });
      toast({ title: 'Student updated', description: `${data.first_name} ${data.last_name} has been updated successfully.`, variant: 'success' });
      setDrawerOpen(false); refetchStudent();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || err.response?.data?.message || 'Failed to update student.', variant: 'destructive' });
    }
  };

  const confirmCancelStudent = async () => {
    if (!student) return;
    try {
      await apiClient.delete(`/api/students/students/${student.uuid}/`);
      toast({ title: t('students.cancelled', 'Student Cancelled'), description: t('students.cancelSuccess', { studentName: `${student.first_name} ${student.last_name}` }), variant: 'success' });
      setCancelDialogOpen(false);
      invalidateQueries.friendships();
      invalidateQueries.students();
      await refetchStudent();
    } catch {
      toast({ title: t('common.error', 'Error'), description: t('students.cancelError', 'Failed to cancel student.'), variant: 'destructive' });
    }
  };

  const confirmReinstateStudent = async () => {
    if (!student) return;
    try {
      await apiClient.patch(`/api/students/students/${student.uuid}/`, { status: 'ACTIVE' });
      toast({ title: t('students.reinstated', 'Student Reinstated'), description: t('students.reinstateSuccess', { studentName: `${student.first_name} ${student.last_name}` }), variant: 'success' });
      setReinstateDialogOpen(false); await refetchStudent();
    } catch {
      toast({ title: t('common.error', 'Error'), description: t('students.reinstateError', 'Failed to reinstate student.'), variant: 'destructive' });
    }
  };

  const confirmFlagStudent = async () => {
    if (!student || !flagNote.trim()) return;
    try {
      await flagStudentMutation.mutateAsync({ studentUuid: student.uuid, message: flagNote.trim() });
      toast({ title: t('students.flag.success', 'Student Flagged'), description: t('students.flag.successMessage', 'The student has been flagged for action.'), variant: 'success' });
      setFlagDialogOpen(false);
      setFlagNote('');
    } catch {
      toast({ title: t('common.error', 'Error'), description: t('students.flag.error', 'Failed to flag student. Please try again.'), variant: 'destructive' });
    }
  };

  const handleResolveIssues = async () => {
    try {
      await apiClient.post(`/api/students/bulk-upload/issues/resolve/${student.student_id}/`);
      toast({ title: t('students.uploadIssues.resolved', 'Issues Resolved'), description: t('students.uploadIssues.resolvedMessage', 'Upload issues have been marked as resolved.'), variant: 'success' });
      setResolveDialogOpen(false); await refetchStudent();
    } catch {
      toast({ title: t('common.error', 'Error'), description: t('students.uploadIssues.resolveFailed', 'Failed to resolve issues.'), variant: 'destructive' });
    }
  };

  const formatDate = (d) => {
    if (!d) return t('students.labels.notProvided');
    return new Date(d).toLocaleDateString('en-IE', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const calculateAge = (birthday) => {
    if (!birthday) return t('students.labels.notProvided');
    const today = new Date(); const birth = new Date(birthday);
    const age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    return (m < 0 || (m === 0 && today.getDate() < birth.getDate())) ? age - 1 : age;
  };

  const getStatusBadge = (status) => {
    const map = {
      'ACTIVE':    { className: 'bg-green-600 text-white border-transparent', label: t('students.status.active') },
      'COMPLETED': { className: 'bg-blue-600 text-white border-transparent',  label: t('students.status.completed') },
      'ARCHIVED':  { className: 'bg-gray-500 text-white border-transparent',  label: t('students.status.archived') },
      'CANCELLED': { className: 'bg-red-600 text-white border-transparent',   label: t('students.status.cancelled') },
    };
    const s = map[status] || { className: 'bg-gray-400 text-white border-transparent', label: status || t('students.status.unknown') };
    return <Badge className={s.className}>{s.label}</Badge>;
  };

  // Loading / error / not-found
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-[#F28482]" />
          <p className="text-muted-foreground text-sm">{t('students.loading.details')}</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-64 text-center">
        <div>
          <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
          <h3 className="font-semibold text-destructive">{t('students.error.loadFailed')}</h3>
          <p className="text-muted-foreground text-sm mb-4">{error?.message || t('students.error.loadMessage')}</p>
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={() => refetchStudent()}>{t('students.error.tryAgain')}</Button>
            <Button onClick={() => navigate('/students')}>{t('students.backToStudents')}</Button>
          </div>
        </div>
      </div>
    );
  }
  if (!student) {
    return (
      <div className="flex items-center justify-center min-h-64 text-center">
        <div>
          <User className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-semibold">{t('students.error.notFound')}</h3>
          <p className="text-muted-foreground text-sm mb-4">{t('students.error.notFoundMessage')}</p>
          <Button onClick={() => navigate('/students')}>{t('students.backToStudents')}</Button>
        </div>
      </div>
    );
  }

  const isCancelled = student.status === 'CANCELLED';

  const initials =
    ((student.first_name?.[0] || '').toUpperCase()) +
    ((student.last_name?.[0] || '').toUpperCase());

  // ── JSX ───────────────────────────────────────────────────────────────────
  return (
    <div className="py-4 space-y-4">

      {/* ── Upload Issues / Action Required Banner ──────────────────────── */}
      {student && (
        student.upload_status === 'IMPORT_WARNING' ||
        student.upload_status === 'IMPORT_ERROR' ||
        student.upload_status === 'ACTION_REQUIRED'
      ) && student.upload_issues?.length > 0 && (() => {
        const isError  = student.upload_status === 'IMPORT_ERROR';
        const isAction = student.upload_status === 'ACTION_REQUIRED';
        const bannerCls   = isError  ? 'bg-destructive/5 border-destructive/30'
                          : isAction ? 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800'
                          :            'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800';
        const iconCls     = isError  ? 'text-destructive'
                          : isAction ? 'text-blue-600 dark:text-blue-400'
                          :            'text-amber-600 dark:text-amber-400';
        const titleCls    = isError  ? 'text-destructive'
                          : isAction ? 'text-blue-900 dark:text-blue-200'
                          :            'text-amber-900 dark:text-amber-200';
        const subtitleCls = isError  ? 'text-destructive/70'
                          : isAction ? 'text-blue-700 dark:text-blue-400'
                          :            'text-amber-700 dark:text-amber-400';
        const dividerCls  = isError  ? 'border-destructive/20'
                          : isAction ? 'border-blue-200 dark:border-blue-800'
                          :            'border-amber-200 dark:border-amber-800';
        const issueCls    = isError  ? 'bg-destructive/10 text-destructive border-destructive/20'
                          : isAction ? 'bg-blue-100/60 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-200/60 dark:border-blue-800/60'
                          :            'bg-amber-100/60 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border-amber-200/60 dark:border-amber-800/60';
        const title       = isError  ? t('students.uploadIssues.hasErrors')
                          : isAction ? t('students.uploadIssues.hasActions', 'Action Required')
                          :            t('students.uploadIssues.hasWarnings');
        return (
          <div className={`border rounded-lg ${bannerCls}`}>
            <div className="p-4 flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <AlertCircle className={`h-5 w-5 mt-0.5 shrink-0 ${iconCls}`} />
                <div>
                  <h3 className={`font-semibold text-sm ${titleCls}`}>{title}</h3>
                  {!isAction && (
                    <p className={`text-xs mt-0.5 ${subtitleCls}`}>
                      {t('students.uploadIssues.issuesCount', { count: student.upload_issues.length })}
                    </p>
                  )}
                </div>
              </div>
              {['ADMIN', 'OFFICE'].includes(userType) && !isCancelled && (
                <Button size="sm" variant="outline" className="bg-white/60 border-black/20 text-gray-800 hover:bg-white/80 dark:bg-white/60 dark:border-black/20 dark:text-gray-800 dark:hover:bg-white/80" onClick={() => setFlagDialogOpen(true)}>
                  <Flag className="h-4 w-4 mr-1.5" />
                  {t('students.flag.button', 'Add Action Note')}
                </Button>
              )}
            </div>
            <div className={`border-t px-4 pb-4 pt-3 space-y-2 ${dividerCls}`}>
              {student.upload_issues.map((issue, idx) => (
                <div key={idx} className={`p-3 rounded-lg border ${issueCls} flex items-start justify-between gap-3`}>
                  <div className="flex-1 min-w-0">
                    {issue.field && issue.field !== 'manual' && (
                      <div className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">{issue.field}</div>
                    )}
                    <div className="text-sm">{issue.message}</div>
                  </div>
                  {permissions.canEdit && !isCancelled && (
                    <button
                      onClick={() => resolveIssueMutation.mutate({ studentUuid: student.uuid, index: idx })}
                      disabled={resolveIssueMutation.isPending}
                      className="shrink-0 opacity-50 hover:opacity-100 transition-opacity"
                      title={t('students.uploadIssues.resolveIssue', 'Dismiss')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── Flag for Review Dialog ───────────────────────────────────────── */}
      <Dialog open={flagDialogOpen} onOpenChange={(open) => { setFlagDialogOpen(open); if (!open) setFlagNote(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="h-4 w-4 text-blue-600" />
              {t('students.flag.title', 'Add Action Note')}
            </DialogTitle>
            <DialogDescription>
              {t('students.flag.description', 'Add a note explaining what action is required. This will mark the student as Action Required and appear in their record.')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Textarea
              placeholder={t('students.flag.notePlaceholder', 'Describe what action is needed…')}
              value={flagNote}
              onChange={(e) => setFlagNote(e.target.value)}
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground mt-1 text-right">{flagNote.length}/500</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setFlagDialogOpen(false); setFlagNote(''); }}>{t('common.cancel', 'Cancel')}</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={confirmFlagStudent}
              disabled={!flagNote.trim() || flagStudentMutation.isPending}
            >
              {flagStudentMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Flag className="h-4 w-4 mr-1.5" />}
              {t('students.flag.confirm', 'Add Action Note')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Hero strip ──────────────────────────────────────────────────── */}
      <div
        className="rounded-xl px-5 py-4"
        style={{ background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            {student.photo_url ? (
              <div className="h-16 w-16 rounded-xl overflow-hidden border-2 border-black/10 shrink-0">
                <img src={student.photo_url} alt={student.full_name} className="h-full w-full object-cover" />
              </div>
            ) : (
              <div className="h-16 w-16 rounded-xl bg-black/10 flex items-center justify-center border-2 border-black/10 shrink-0">
                <span className="text-xl font-bold text-gray-800 leading-none">{initials}</span>
              </div>
            )}

            {/* Name + ID + badges */}
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                  {student.first_name} {student.last_name}
                </h1>
                {student.is_cinnire && (
                  <Badge className="bg-amber-600 hover:bg-amber-600 text-white border-transparent">
                    <Award className="h-3 w-3 mr-1" />
                    {t('students.badge.cinnire')}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                <p className="text-gray-700 text-sm font-mono">{student.student_id}</p>
                {getStatusBadge(student.status)}
                {student.house_name && (
                  <Badge variant="outline" className="border-black/20 text-gray-800 bg-white/40 text-xs">
                    <Home className="h-3 w-3 mr-1" />{student.house_name}
                  </Badge>
                )}
                {student.travel_required && (
                  <Badge variant="outline" className="border-black/20 text-gray-800 bg-white/40 text-xs">
                    <Bus className="h-3 w-3 mr-1" />{student.travel_location_name || t('students.badge.travelRequired')}
                  </Badge>
                )}
                {student.has_friends && (
                  <Badge variant="outline" className="border-black/20 text-gray-800 bg-white/40 text-xs">
                    <UserCheck className="h-3 w-3 mr-1" />{t('students.badge.hasFriends')}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" className="bg-white/60 border-black/20 text-gray-800 hover:bg-white/80 dark:bg-white/60 dark:border-black/20 dark:text-gray-800 dark:hover:bg-white/80" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              {t('students.backToStudents', 'Back')}
            </Button>
            {['ADMIN', 'OFFICE'].includes(userType) && !isCancelled && (
              <Button size="sm" variant="outline" className="bg-white/60 border-black/20 text-gray-800 hover:bg-white/80 dark:bg-white/60 dark:border-black/20 dark:text-gray-800 dark:hover:bg-white/80" onClick={() => setFlagDialogOpen(true)}>
                <Flag className="h-4 w-4 mr-1.5" />
                {t('students.flag.button', 'Add Action Note')}
              </Button>
            )}
            {permissions.canEdit && !isCancelled && (
              <Button size="sm" className="bg-white hover:bg-white/90 text-gray-900 border border-black/10" onClick={() => setDrawerOpen(true)}>
                <Edit className="h-4 w-4 mr-1.5" />
                {t('students.edit.editProfile')}
              </Button>
            )}
            {permissions.canEdit && student?.status === 'ACTIVE' && (
              <Button size="sm" variant="destructive" onClick={() => setCancelDialogOpen(true)}>
                <XCircle className="h-4 w-4 mr-1.5" />
                {t('students.labels.cancelEnrollment')}
              </Button>
            )}
            {permissions.canEdit && student?.status === 'CANCELLED' && (
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => setReinstateDialogOpen(true)}>
                <UserCheck className="h-4 w-4 mr-1.5" />
                {t('students.labels.reinstateStudent')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <div className="pb-8 space-y-4">

          {permissions.canEdit && (
            <LinkedHistoryPanel student={student} />
          )}

          {/* Row 1 — Who: Contact + Academic */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {permissions.canViewParentInfo && (
              <SectionCard title={t('students.labels.contactInformation')} icon={Phone}>
                <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('students.labels.parentGuardian')}</p>
                    <p className="text-sm font-medium text-foreground">{student.parent_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Mail className="h-3 w-3" />{t('students.labels.email')}
                    </p>
                    <p className="text-sm text-foreground truncate">{student.parent_email || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Phone className="h-3 w-3" />{t('students.labels.phone')}
                    </p>
                    <p className="text-sm font-medium text-foreground">{student.parent_phone || '—'}</p>
                  </div>
                  {student.parent_account_email && (
                    <div className="col-span-3 pt-2 border-t border-border">
                      <p className="text-xs text-muted-foreground">{t('students.labels.parentAccount')}</p>
                      <p className="text-xs text-green-600 dark:text-green-400">{student.parent_account_email}</p>
                    </div>
                  )}
                  {permissions.canViewFullAddress && (student.address_1 || student.address_2) && (
                    <div className="col-span-3 pt-2 border-t border-border">
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                        <MapPin className="h-3 w-3" />{t('students.detail.address')}
                      </p>
                      {[student.address_1, student.address_2, student.address_3, student.address_4].filter(Boolean).map((l, i) => (
                        <p key={i} className="text-xs text-foreground">{l}</p>
                      ))}
                      {student.eircode && <p className="text-xs font-mono text-foreground">{student.eircode}</p>}
                    </div>
                  )}
                </div>
              </SectionCard>
            )}

            <SectionCard title={t('students.detail.academicInfo')} icon={GraduationCap}>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('students.form.dateOfBirth')}</p>
                    <p className="text-sm font-medium text-foreground">{formatDate(student.birthday)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('students.form.age')}</p>
                    <p className="text-sm font-medium text-foreground">{calculateAge(student.birthday)} {t('students.labels.yearsOld')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('students.form.gender')}</p>
                    <p className="text-sm font-medium text-foreground">
                      {student.gender === 'M' ? t('students.labels.male')
                      : student.gender === 'F' ? t('students.labels.female')
                      : student.gender === 'O' ? t('students.labels.other') : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('students.form.homeCounty')}</p>
                    <p className="text-sm font-medium text-foreground">{student.home_county || '—'}</p>
                  </div>
                </div>
                {student.student_email && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t('students.form.studentEmail')}</p>
                    <p className="text-sm text-foreground break-all">{student.student_email}</p>
                  </div>
                )}
                <div className="border-t border-border pt-3 grid grid-cols-3 gap-x-4 gap-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('students.form.course')}</p>
                    <p className="text-sm text-foreground">{student.course_name || student.course?.name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('students.form.school')}</p>
                    <p className="text-sm text-foreground">{student.school || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('students.form.schoolYear')}</p>
                    <p className="text-sm text-foreground">{student.school_year_name || student.school_year?.year || '—'}</p>
                  </div>
                  {student.attended_before && (
                    <div className="col-span-3 pt-1 border-t border-border">
                      <Badge variant="outline" className="text-xs">{t('students.badge.previouslyAttended')}</Badge>
                      {student.when_attended && <p className="text-xs text-muted-foreground mt-1">{student.when_attended}</p>}
                    </div>
                  )}
                </div>
              </div>
            </SectionCard>
          </div>

          {/* Row 2 — Placement: House · Class · Travel & Leadership */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <HouseAssignmentPanel student={student} canEdit={permissions.canEdit && !isCancelled} />
            <ClassAssignmentPanel student={student} canEdit={permissions.canEdit && !isCancelled} />
            <SectionCard title={t('students.detail.travelLeadership')} icon={Bus}>
              <div className="divide-y divide-border">
                <div className="flex items-center justify-between py-3 first:pt-0">
                  <p className="text-xs text-muted-foreground">{t('students.form.travelRequired')}</p>
                  {student.travel_required ? (
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <Badge className="bg-sky-500 hover:bg-sky-500 text-xs">
                        <Bus className="h-3 w-3 mr-1" />{t('students.badge.yes')}
                      </Badge>
                      {(student.travel_location_name || student.travel_location?.name) && (
                        <span className="text-sm flex items-center gap-1 text-foreground">
                          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                          {student.travel_location_name || student.travel_location?.name}
                        </span>
                      )}
                    </div>
                  ) : (
                    <Badge variant="outline" className="text-xs">{t('students.badge.no')}</Badge>
                  )}
                </div>
                <div className="flex items-center justify-between py-3">
                  <p className="text-xs text-muted-foreground">{t('students.form.leadershipRole')}</p>
                  {student.is_cinnire ? (
                    <Badge className="bg-amber-500 hover:bg-amber-500 text-xs">
                      <Award className="h-3 w-3 mr-1" />{t('students.badge.cinnire')}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs">{t('students.badge.none')}</Badge>
                  )}
                </div>
                {student.oide_username && (
                  <div className="flex items-center justify-between py-3 last:pb-0">
                    <p className="text-xs text-muted-foreground">{t('students.form.assignedStaff')}</p>
                    <span className="font-mono text-xs bg-muted px-2 py-1 rounded">{student.oide_username}</span>
                  </div>
                )}
              </div>
            </SectionCard>
          </div>

          {/* Row 3 — People: Friends + Friend Group | Interview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-4">
              <FriendshipPanel student={student} readOnly={isCancelled || userType === 'INTERVIEWER'} />
              <FriendGroupCard student={student} />
            </div>
            {permissions.canScoreInterviews && (
              <InterviewScoringPanel student={student} onScoreUpdated={refetchStudent} readOnly={isCancelled} />
            )}
          </div>

          {/* Tail — full width */}
          {permissions.canViewFinancials && (
            <PaymentsPanel student={student} readOnly={isCancelled} />
          )}
          {permissions.canViewCommunications && (
            <EmailLogPanel student={student} />
          )}

          {/* Row 6: Notes */}
          {permissions.canViewNotes && (
            <SectionCard title={t('students.detail.notes')} icon={MessageSquare}>
              <p className="text-sm whitespace-pre-wrap text-muted-foreground min-h-[60px] leading-relaxed">
                {student.notes || t('students.labels.noNotes')}
              </p>
            </SectionCard>
          )}

      </div>

      {/* ── Edit Profile Drawer ─────────────────────────────────────────── */}
      <Sheet open={drawerOpen} onOpenChange={(open) => {
        if (!open && form.formState.isDirty) {
          if (!window.confirm(t('students.unsavedChanges.discard'))) return;
          form.reset();
        }
        setDrawerOpen(open);
      }}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>
              <Edit className="h-4 w-4 inline mr-2" />
              {t('students.edit.editProfile')} — {student.first_name} {student.last_name}
            </SheetTitle>
            <SheetDescription>{t('students.edit.editProfileDesc', 'Update student details below')}</SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
            <Form {...form}>

              <div className="space-y-4">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border pb-2 flex items-center gap-2">
                  <User className="h-4 w-4" />{t('students.detail.personalInfo')}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="first_name" render={({ field }) => (
                    <FormItem><FormLabel>{t('students.form.firstName')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="last_name" render={({ field }) => (
                    <FormItem><FormLabel>{t('students.form.lastName')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="birthday" render={({ field }) => (
                    <FormItem><FormLabel>{t('students.form.dateOfBirth')}</FormLabel><FormControl><Input {...field} type="date" /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="gender" render={({ field }) => (
                    <FormItem><FormLabel>{t('students.form.gender')}</FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger><SelectValue placeholder={t('students.placeholders.selectGender')} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="M">{t('students.labels.male')}</SelectItem>
                            <SelectItem value="F">{t('students.labels.female')}</SelectItem>
                            <SelectItem value="O">{t('students.labels.other')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl><FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="home_county" render={({ field }) => (
                    <FormItem><FormLabel>{t('students.form.homeCounty')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="student_email" render={({ field }) => (
                    <FormItem className="col-span-2"><FormLabel>{t('students.form.studentEmail')}</FormLabel><FormControl><Input {...field} type="email" /></FormControl><FormMessage /></FormItem>
                  )} />
                </div>
              </div>

              {permissions.canViewParentInfo && (
                <div className="space-y-4">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border pb-2 flex items-center gap-2">
                    <Phone className="h-4 w-4" />{t('students.labels.contactInformation')}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField control={form.control} name="parent_name" render={({ field }) => (
                      <FormItem className="col-span-2"><FormLabel>{t('students.labels.parentGuardian')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="parent_email" render={({ field }) => (
                      <FormItem><FormLabel>{t('students.labels.email')}</FormLabel><FormControl><Input {...field} type="email" /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="parent_phone" render={({ field }) => (
                      <FormItem><FormLabel>{t('students.labels.phone')}</FormLabel><FormControl><Input {...field} type="tel" /></FormControl><FormMessage /></FormItem>
                    )} />
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border pb-2 flex items-center gap-2">
                  <GraduationCap className="h-4 w-4" />{t('students.detail.academicInfo')}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="course" render={({ field }) => (
                    <FormItem><FormLabel>{t('students.form.course')}</FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger><SelectValue placeholder={t('students.placeholders.selectCourse')} /></SelectTrigger>
                          <SelectContent>{courses?.map(c => (
                            <SelectItem key={c.uuid} value={c.uuid} disabled={c.has_archived_students}>
                              {c.name}{c.has_archived_students && ` (${t('courses.archive.archivedBadge', 'Archived')})`}
                            </SelectItem>
                          ))}</SelectContent>
                        </Select>
                      </FormControl><FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="school_year" render={({ field }) => (
                    <FormItem><FormLabel>{t('students.form.schoolYear')}</FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger><SelectValue placeholder={t('students.placeholders.selectSchoolYear')} /></SelectTrigger>
                          <SelectContent>{schoolYears?.map(sy => <SelectItem key={sy.uuid} value={sy.uuid}>{sy.year}</SelectItem>)}</SelectContent>
                        </Select>
                      </FormControl><FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="school" render={({ field }) => (
                    <FormItem className="col-span-2"><FormLabel>{t('students.form.school')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <div className="col-span-2">
                    <FormField control={form.control} name="attended_before" render={({ field }) => (
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                        <FormLabel className="cursor-pointer">{t('students.form.previousAttendance')}</FormLabel>
                      </FormItem>
                    )} />
                  </div>
                  {form.watch('attended_before') && (
                    <FormField control={form.control} name="when_attended" render={({ field }) => (
                      <FormItem className="col-span-2"><FormLabel>{t('students.form.whenAttended', 'When attended')}</FormLabel><FormControl><Input {...field} placeholder="e.g. Summer 2023" /></FormControl><FormMessage /></FormItem>
                    )} />
                  )}
                </div>
              </div>

              {permissions.canViewFullAddress && (
                <div className="space-y-4">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border pb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />{t('students.detail.address')}
                  </h3>
                  <div className="space-y-3">
                    {['address_1','address_2','address_3','address_4'].map((name, i) => (
                      <FormField key={name} control={form.control} name={name} render={({ field }) => (
                        <FormItem><FormLabel className="text-xs">{t(`students.form.addressLine${i+1}`)}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                    ))}
                    <FormField control={form.control} name="eircode" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">{t('students.form.eircode')}</FormLabel><FormControl><Input {...field} className="font-mono" /></FormControl><FormMessage /></FormItem>
                    )} />
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border pb-2 flex items-center gap-2">
                  <Bus className="h-4 w-4" />{t('students.detail.travelLeadership')}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="travel_required" render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                      <FormLabel className="cursor-pointer">{t('students.form.travelRequired')}</FormLabel>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="is_cinnire" render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                      <FormLabel className="cursor-pointer">{t('students.form.leadershipRole')}</FormLabel>
                    </FormItem>
                  )} />
                  {form.watch('travel_required') && (
                    <FormField control={form.control} name="travel_location" render={({ field }) => (
                      <FormItem className="col-span-2"><FormLabel>{t('students.form.travelLocation')}</FormLabel>
                        <FormControl>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <SelectTrigger><SelectValue placeholder={t('students.placeholders.selectTravelLocation')} /></SelectTrigger>
                            <SelectContent>{travelLocations?.map(l => <SelectItem key={l.uuid} value={l.uuid}>{l.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </FormControl><FormMessage />
                      </FormItem>
                    )} />
                  )}
                  <FormField control={form.control} name="has_friends" render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                      <FormLabel className="cursor-pointer">{t('students.form.hasFriends', 'Has friends')}</FormLabel>
                    </FormItem>
                  )} />
                </div>
              </div>

              {permissions.canViewNotes && (
                <div className="space-y-4">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border pb-2 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />{t('students.detail.notes')}
                  </h3>
                  <FormField control={form.control} name="notes" render={({ field }) => (
                    <FormItem><FormControl><Textarea {...field} rows={4} placeholder={t('students.placeholders.addNotes')} /></FormControl><FormMessage /></FormItem>
                  )} />
                </div>
              )}

            </Form>
          </div>

          <SheetFooter>
            <Button variant="outline" onClick={() => {
              if (form.formState.isDirty && !window.confirm(t('students.unsavedChanges.discard'))) return;
              form.reset(); setDrawerOpen(false);
            }}>
              {t('students.edit.cancelChanges')}
            </Button>
            <Button
              onClick={form.handleSubmit(handleSave, (errs) => {
                const el = document.getElementsByName(Object.keys(errs)[0])[0];
                if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
              })}
              disabled={updateStudentMutation.isPending}
            >
              {updateStudentMutation.isPending
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('students.edit.saving')}</>
                : <><Save className="h-4 w-4 mr-2" />{t('students.edit.saveChanges')}</>
              }
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('students.cancel.title', 'Cancel Student Enrollment')}</DialogTitle>
            <DialogDescription>
              {t('students.cancel.message', "Are you sure you want to cancel {{studentName}}'s enrollment? This action can be reversed later by changing their status.", { studentName: student ? `${student.first_name} ${student.last_name}` : '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button variant="destructive" onClick={confirmCancelStudent}>{t('students.cancelEnrollment', 'Cancel Enrollment')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reinstate Dialog */}
      <Dialog open={reinstateDialogOpen} onOpenChange={setReinstateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('students.reinstate.title', 'Reinstate Student')}</DialogTitle>
            <DialogDescription>
              {t('students.reinstate.message', "Are you sure you want to reinstate {{studentName}}? Their status will be changed back to ACTIVE.", { studentName: student ? `${student.first_name} ${student.last_name}` : '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReinstateDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={confirmReinstateStudent}>{t('students.reinstateButton', 'Reinstate Student')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve Issues Dialog */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className={`h-5 w-5 ${student?.upload_status === 'IMPORT_ERROR' ? 'text-destructive' : 'text-amber-600'}`} />
              {t('students.uploadIssues.confirmResolveTitle', 'Mark Issues as Resolved')}
            </DialogTitle>
            <DialogDescription>
              {t('students.uploadIssues.confirmResolve', 'Are you sure you want to mark these upload issues as resolved?')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button
              onClick={handleResolveIssues}
              className={student?.upload_status === 'IMPORT_ERROR' ? 'bg-red-600 hover:bg-red-700' : 'bg-yellow-600 hover:bg-yellow-700'}
            >
              {t('students.uploadIssues.markResolved', 'Mark Resolved')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentDetailNew;
